/* eslint-disable react/no-unescaped-entities */
// ─────────────────────────────────────────────────────────────
// Get Started page, app picker, install steps, blog teaser.
// Ported from the Claude Design mock; CTAs wired to real install URLs.
// ─────────────────────────────────────────────────────────────
import React, { useState, useEffect } from "react";
import Link from "next/link";
import { APPS } from "@buddieshr/components/brandLogos";
import { PRODUCTS } from "@buddieshr/utils/constants-products";
import { getInstallLink } from "@buddieshr/utils/utils";
import { postWaitlist } from "@buddieshr/utils/api";
import { gtagEvent } from "@buddieshr/utils/gtag";
import { oaiqCheckoutStarted } from "@buddieshr/utils/oaiq";
import {
  ALFY_MICROSOFT_TEAMS_LINK,
  ANALYTICS_EVENTS,
  MICROSOFT_TEAMS_LINK,
} from "@buddieshr/utils/constants";

// Apps already live on Microsoft Teams → their marketplace listing.
const TEAMS_LINK_BY_APP = {
  alfy: ALFY_MICROSOFT_TEAMS_LINK,
  billy: MICROSOFT_TEAMS_LINK,
};
const PERF_SLACK_LINK =
  "https://performance-management-api.buddieshr.com/web-app/auth/register-with-slack";

// slug → Slack install URL (from the real product catalog)
const INSTALL_BY_SLUG = Object.fromEntries(
  PRODUCTS.filter((p) => p.installSlackLink).map((p) => [
    p.slug,
    p.installSlackLink,
  ])
);

// ═══ TEAMS WAITLIST MODAL ═══
function GSWaitlistModal({ app, onClose }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done | error

  // Reset whenever a new app opens the modal.
  useEffect(() => {
    setEmail("");
    setStatus("idle");
  }, [app]);

  // Close on Escape.
  useEffect(() => {
    if (!app) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [app, onClose]);

  if (!app) return null;
  const Logo = app.Logo;
  const valid = /\S+@\S+\.\S+/.test(email);

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || status === "loading") return;
    setStatus("loading");
    try {
      await postWaitlist({
        email: email.trim(),
        app: app.id,
        platform: "teams",
      });
      setStatus("done");
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(31,27,22,.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        animation: "gsModalFade .2s ease",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 420,
          background: "var(--bh-cream)",
          border: "1.5px solid var(--bh-line)",
          borderRadius: "var(--bh-r-lg)",
          padding: "30px 30px 28px",
          boxShadow: "0 24px 60px rgba(31,27,22,.3)",
          textAlign: "center",
          animation: "gsModalIn .25s cubic-bezier(.4,0,.2,1)",
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute",
            top: 14,
            right: 16,
            background: "none",
            border: "none",
            fontSize: 22,
            lineHeight: 1,
            cursor: "pointer",
            color: "var(--bh-ink-mute)",
            padding: 0,
          }}
        >
          ×
        </button>

        {status === "done" ? (
          <>
            <div style={{ fontSize: 40, marginBottom: 8 }}>🎉</div>
            <h3
              style={{
                fontFamily: "var(--bh-font-display)",
                fontSize: 24,
                fontWeight: 600,
                letterSpacing: "-0.02em",
                color: "var(--bh-ink)",
                margin: "0 0 8px",
              }}
            >
              You&apos;re on the list!
            </h3>
            <p style={{ fontSize: 15, color: "var(--bh-ink-soft)", lineHeight: 1.5, margin: "0 0 20px" }}>
              We&apos;ll email you the moment {app.name} goes live on Microsoft
              Teams.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="bh-btn primary"
              style={{ width: "100%", justifyContent: "center", fontSize: 14, padding: "11px 14px" }}
            >
              Done
            </button>
          </>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
              <span style={{ width: 30, height: 30 }}>
                <Logo />
              </span>
              <img
                src="/img/redesign/teams-logo.png"
                alt=""
                style={{ width: 30, height: 30, marginLeft: -6 }}
              />
            </div>
            <h3
              style={{
                fontFamily: "var(--bh-font-display)",
                fontSize: 23,
                fontWeight: 600,
                letterSpacing: "-0.02em",
                color: "var(--bh-ink)",
                margin: "0 0 8px",
              }}
            >
              {app.name} is coming to Teams
            </h3>
            <p style={{ fontSize: 15, color: "var(--bh-ink-soft)", lineHeight: 1.5, margin: "0 0 20px" }}>
              We&apos;re rolling out {app.name} for Microsoft Teams. Leave your
              email and we&apos;ll let you know the second it&apos;s ready.
            </p>
            <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                aria-label="Email address"
                autoFocus
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  fontSize: 15,
                  borderRadius: "var(--bh-r-md)",
                  border: "1.5px solid var(--bh-line-strong)",
                  background: "#fff",
                  color: "var(--bh-ink)",
                  outline: "none",
                }}
              />
              <button
                type="submit"
                className="bh-btn primary"
                disabled={!valid || status === "loading"}
                style={{
                  width: "100%",
                  justifyContent: "center",
                  fontSize: 14,
                  padding: "11px 14px",
                  opacity: valid && status !== "loading" ? 1 : 0.6,
                  cursor: valid && status !== "loading" ? "pointer" : "not-allowed",
                }}
              >
                {status === "loading" ? "Adding you…" : "Keep me in the loop"}
              </button>
              {status === "error" && (
                <div style={{ fontSize: 13, color: "var(--bh-coral-400)" }}>
                  Something went wrong. Please try again.
                </div>
              )}
            </form>
            <p style={{ fontSize: 12, color: "var(--bh-ink-mute)", margin: "14px 0 0" }}>
              It works in Slack today, install it now if you can&apos;t wait.
            </p>
          </>
        )}
      </div>
      <style>{`
        @keyframes gsModalFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes gsModalIn { from { opacity: 0; transform: translateY(12px) scale(.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
      `}</style>
    </div>
  );
}

// ═══ ONE APP CARD ═══
function GSAppCard({ app, verb, desc, slackHref, registerHref, isPerf, onTeamsClick }) {
  const teamsReady = Boolean(TEAMS_LINK_BY_APP[app.id]);
  const Logo = app.Logo;
  return (
    <div
      style={{
        background: app.bg,
        border: "1.5px solid var(--bh-line)",
        borderRadius: "var(--bh-r-lg)",
        padding: "26px 22px 22px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: 0,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: ".12em",
          textTransform: "uppercase",
          color: "var(--bh-ink-mute)",
          marginBottom: 16,
        }}
      >
        {verb}
      </div>
      <div style={{ width: 84, height: 84 }}>
        <Logo />
      </div>
      <div
        style={{
          fontFamily: "var(--bh-font-display)",
          fontSize: 24,
          fontWeight: 600,
          letterSpacing: "-0.02em",
          color: "var(--bh-ink)",
          marginTop: 14,
        }}
      >
        {app.name}
      </div>
      <p
        style={{
          fontSize: 14,
          color: "var(--bh-ink-soft)",
          lineHeight: 1.45,
          margin: "6px 0 18px",
          minHeight: 40,
        }}
      >
        {desc}
      </p>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          width: "100%",
          marginTop: "auto",
        }}
      >
        {isPerf ? (
          <>
            <a
              href={slackHref}
              className="bh-btn primary"
              onClick={() => {
                gtagEvent({
                  action: `${ANALYTICS_EVENTS.ADD_TO_SLACK}_${app.id.toUpperCase()}`,
                  category: "register",
                  label: "GetStartedAppCard",
                });
                gtagEvent({
                  action: ANALYTICS_EVENTS.ADD_TO_SLACK,
                  category: "register",
                  label: "GetStartedAppCard",
                });
                oaiqCheckoutStarted({ id: app.id, name: app.name });
              }}
              style={{
                width: "100%",
                justifyContent: "center",
                fontSize: 14,
                padding: "10px 14px",
                textDecoration: "none",
              }}
            >
              <img
                src="/img/redesign/slack-logo.png"
                alt=""
                style={{ width: 17, height: 17, marginRight: 8, verticalAlign: "-3px" }}
              />
              Add to Slack
            </a>
            <Link
              href={registerHref}
              className="bh-btn secondary"
              onClick={() => {
                gtagEvent({
                  action: ANALYTICS_EVENTS.REGISTER_SIMPLEPERF,
                  category: "register",
                  label: "GetStartedAppCard",
                });
              }}
              style={{
                width: "100%",
                justifyContent: "center",
                fontSize: 14,
                padding: "10px 14px",
                textDecoration: "none",
              }}
            >
              Use web only <span className="bh-arrow">→</span>
            </Link>
          </>
        ) : (
          <>
            <a
              href={slackHref}
              className="bh-btn primary"
              onClick={() => {
                gtagEvent({
                  action: `${ANALYTICS_EVENTS.ADD_TO_SLACK}_${app.id.toUpperCase()}`,
                  category: "register",
                  label: "GetStartedAppCard",
                });
                gtagEvent({
                  action: ANALYTICS_EVENTS.ADD_TO_SLACK,
                  category: "register",
                  label: "GetStartedAppCard",
                });
                oaiqCheckoutStarted({ id: app.id, name: app.name });
              }}
              style={{
                width: "100%",
                justifyContent: "center",
                fontSize: 14,
                padding: "10px 14px",
                textDecoration: "none",
              }}
            >
              <img
                src="/img/redesign/slack-logo.png"
                alt=""
                style={{ width: 17, height: 17, marginRight: 8, verticalAlign: "-3px" }}
              />
              Add to Slack
            </a>
            <button
              type="button"
              className="bh-btn secondary"
              onClick={() => onTeamsClick(app)}
              style={{
                width: "100%",
                justifyContent: "center",
                fontSize: 14,
                padding: "10px 14px",
                opacity: teamsReady ? 1 : 0.75,
              }}
            >
              <img
                src="/img/redesign/teams-logo.png"
                alt=""
                style={{ width: 17, height: 17, marginRight: 8, verticalAlign: "-3px" }}
              />
              Add to Teams
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ═══ HERO + APP PICKER ═══
export function GSHero({ refSource = "landing_buddieshr" }) {
  const [teamsApp, setTeamsApp] = useState(null);
  const byId = Object.fromEntries(APPS.map((a) => [a.id, a]));

  const CARDS = [
    { id: "alfy", verb: "Connect", desc: "Connect your team for coffee chats" },
    { id: "billy", verb: "Celebrate", desc: "Celebrate birthdays and work anniversaries" },
    { id: "clappy", verb: "Recognize", desc: "Recognize employees, promote company values" },
    { id: "pulsy", verb: "Measure", desc: "Run polls, surveys, eNPS" },
    { id: "linky", verb: "Organize", desc: "Build your org chart & employee directory" },
    { id: "stany", verb: "Sync", desc: "Automate daily check-ins" },
    { id: "palmy", verb: "Coordinate", desc: "Streamline vacation requests" },
    { id: "perf", verb: "Perform", desc: "360° performance reviews" },
  ];

  const onTeamsClick = (app) => {
    // Alfy and Billy are live on Teams; everything else collects a waitlist email.
    if (TEAMS_LINK_BY_APP[app.id]) {
      window.open(TEAMS_LINK_BY_APP[app.id], "_blank", "noopener,noreferrer");
    } else {
      setTeamsApp(app);
    }
  };

  return (
    <section className="bh-section-hero" style={{ position: "relative", overflow: "hidden" }}>
      <div
        className="bh-blob"
        style={{
          position: "absolute",
          top: -120,
          right: "-6%",
          width: 480,
          height: 480,
          background: "var(--app-billy-bg)",
          opacity: 0.8,
        }}
      />
      <div
        className="bh-blob"
        style={{
          position: "absolute",
          top: 240,
          left: "-8%",
          width: 420,
          height: 420,
          background: "var(--app-pulsy-bg)",
          opacity: 0.7,
        }}
      />

      <div style={{ maxWidth: 1180, margin: "0 auto", position: "relative" }}>
        <div style={{ textAlign: "center", maxWidth: 780, margin: "0 auto 56px" }}>
          <div className="bh-chip" style={{ marginBottom: 18 }}>
            🚀 Free to start · no sales call
          </div>
          <h1
            style={{
              fontSize: "clamp(38px, 5.6vw, 68px)",
              letterSpacing: "-0.035em",
              lineHeight: 1.02,
              margin: 0,
            }}
          >
            Your People Platform,
            <br />
            <span style={{ fontStyle: "italic", color: "var(--bh-coral-400)" }}>
              live before lunch.
            </span>
          </h1>
          <p
            style={{
              fontSize: 18,
              color: "var(--bh-ink-soft)",
              marginTop: 20,
              lineHeight: 1.55,
              maxWidth: 620,
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            Every BuddiesHR app installs straight into Slack or Microsoft Teams in
            about three minutes. No IT ticket, no onboarding project, no credit
            card.
          </p>
        </div>

        <h2
          style={{
            textAlign: "center",
            fontSize: "clamp(22px, 2.6vw, 30px)",
            letterSpacing: "-0.02em",
            marginBottom: 28,
          }}
        >
          Which app would you like to{" "}
          <span style={{ fontStyle: "italic", color: "var(--bh-coral-400)" }}>
            install
          </span>{" "}
          first?
        </h2>

        <div
          className="gs-grid"
          style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}
        >
          {CARDS.map((c) => {
            const app = byId[c.id];
            if (!app) return null;
            const isPerf = c.id === "perf";
            return (
              <GSAppCard
                key={c.id}
                app={app}
                verb={c.verb}
                desc={c.desc}
                isPerf={isPerf}
                registerHref={`/simpleperf-register?ref=${refSource}`}
                slackHref={
                  isPerf
                    ? `${PERF_SLACK_LINK}?ref=${refSource}`
                    : getInstallLink(refSource, INSTALL_BY_SLUG[c.id])
                }
                onTeamsClick={onTeamsClick}
              />
            );
          })}
        </div>

        <p
          style={{
            textAlign: "center",
            fontSize: 13,
            color: "var(--bh-ink-mute)",
            marginTop: 18,
          }}
        >
          Microsoft Teams versions are rolling out app by app. Billy is live
          today, the rest are coming soon.
        </p>
      </div>

      <GSWaitlistModal app={teamsApp} onClose={() => setTeamsApp(null)} />
    </section>
  );
}

// ═══ INSTALLATION PROCESS ═══
export function GSInstall() {
  const chipStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: "#fff",
    border: "1.5px solid var(--bh-line)",
    borderRadius: 10,
    padding: "8px 14px",
    fontWeight: 700,
    fontSize: 13,
    boxShadow: "var(--bh-shadow-sm)",
  };
  const steps = [
    {
      n: "1",
      t: 'Click "Add to Slack"',
      d: "Or \"Add to Teams\". You're sent to the official app marketplace, nothing to download.",
      visual: (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-start" }}>
          <span style={chipStyle}>
            <img src="/img/redesign/slack-logo.png" width={18} height={18} alt="" /> Add to Slack
          </span>
          <span style={{ ...chipStyle, color: "var(--bh-ink-soft)" }}>
            <img src="/img/redesign/teams-logo.png" width={18} height={18} alt="" /> Add to Teams
          </span>
        </div>
      ),
    },
    {
      n: "2",
      t: "Accept permissions",
      d: "One approval screen. We only ask for what the app needs to work.",
      visual: (
        <div style={{
          background: "#fff", border: "1.5px solid var(--bh-line)",
          borderRadius: 10, padding: "12px 14px", boxShadow: "var(--bh-shadow-sm)", width: "100%",
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--bh-ink-mute)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>
            Permissions requested
          </div>
          {["Post messages", "View channels", "Read basic profile"].map((p) => (
            <div key={p} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "var(--bh-ink-soft)", marginBottom: 5 }}>
              <span style={{ color: "var(--app-clappy)", fontWeight: 700 }}>✓</span> {p}
            </div>
          ))}
          <div style={{ marginTop: 8, display: "inline-block", background: "var(--app-clappy)", color: "#fff", borderRadius: 8, padding: "5px 16px", fontSize: 12.5, fontWeight: 700 }}>
            Allow
          </div>
        </div>
      ),
    },
    {
      n: "3",
      t: "Finish setup",
      d: "The app greets you with a 2-minute guided setup, right where your team already is.",
      visual: (
        <div style={{
          background: "#fff", border: "1.5px solid var(--bh-line)",
          borderRadius: 10, padding: "12px 14px", boxShadow: "var(--bh-shadow-sm)", width: "100%",
        }}>
          <div style={{ display: "flex", gap: 10 }}>
            <span style={{ width: 30, height: 30, borderRadius: 8, background: "var(--bh-sun-300)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>
              👋
            </span>
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 700 }}>
                BuddiesHR{" "}
                <span style={{ fontSize: 9, padding: "1px 4px", background: "rgba(31,27,22,.08)", borderRadius: 3, color: "var(--bh-ink-mute)", fontWeight: 700 }}>APP</span>
              </div>
              <div style={{ fontSize: 12.5, color: "var(--bh-ink-soft)", marginTop: 3, lineHeight: 1.4 }}>
                Welcome! Let's set you up, takes 2 minutes.
              </div>
              <div style={{ marginTop: 8 }}>
                <span style={{ display: "inline-block", background: "var(--bh-ink)", color: "var(--bh-cream)", borderRadius: 8, padding: "5px 14px", fontSize: 12, fontWeight: 700 }}>
                  Start setup
                </span>
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ];
  return (
    <section
      className="bh-section"
      style={{
        background: "var(--bh-cream-deep)",
        borderTop: "1px solid var(--bh-line)",
      }}
    >
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 24,
            flexWrap: "wrap",
            marginBottom: 44,
          }}
        >
          <h2
            style={{
              fontSize: "clamp(28px, 3.6vw, 44px)",
              letterSpacing: "-0.03em",
              lineHeight: 1.05,
              margin: 0,
            }}
          >
            Get it live{" "}
            <span style={{ fontStyle: "italic", color: "var(--bh-coral-400)" }}>
              in seconds.
            </span>
          </h2>
          <Link
            href="/docs"
            className="bh-btn secondary"
            style={{
              textDecoration: "none",
              fontSize: 14,
            }}
          >
            🎥 Help & video tutorials
          </Link>
        </div>
        <div
          className="gs-steps"
          style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}
        >
          {steps.map((s, i) => (
            <div
              key={i}
              style={{
                background: "#fff",
                border: "1.5px solid var(--bh-line)",
                borderRadius: "var(--bh-r-lg)",
                padding: "26px 26px 28px",
                boxShadow: "var(--bh-shadow-sm)",
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "var(--bh-coral-400)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "var(--bh-font-display)",
                  fontSize: 20,
                  fontWeight: 600,
                  border: "2px solid var(--bh-ink)",
                  boxShadow: "0 2px 0 var(--bh-ink)",
                  marginBottom: 18,
                }}
              >
                {s.n}
              </div>
              <div
                style={{
                  fontFamily: "var(--bh-font-display)",
                  fontSize: 21,
                  fontWeight: 600,
                  letterSpacing: "-0.015em",
                  marginBottom: 8,
                }}
              >
                {s.t}
              </div>
              <p
                style={{
                  fontSize: 15,
                  color: "var(--bh-ink-soft)",
                  lineHeight: 1.55,
                  margin: 0,
                }}
              >
                {s.d}
              </p>
              <div
                style={{
                  marginTop: 20,
                  paddingTop: 20,
                  borderTop: "1px solid var(--bh-line)",
                  minHeight: 96,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {s.visual}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ═══ BLOG TEASER ═══
export function GSBlog() {
  const byId = Object.fromEntries(APPS.map((a) => [a.id, a]));
  const posts = [
    {
      tag: "Recognition",
      t: "How to send Slack kudos in 3 easy steps with Clappy",
      d: "A practical guide to building a recognition habit your team actually keeps.",
      color: "var(--app-clappy)",
      bg: "var(--app-clappy-bg)",
      logos: ["clappy"],
      href: "https://blog.buddieshr.com/how-to-send-slack-kudos-in-3-easy-steps-with-clappy/",
    },
    {
      tag: "Surveys",
      t: "How to create a survey in Slack [2024]",
      d: "Running surveys where people already work is the only way they get answered.",
      color: "var(--app-pulsy)",
      bg: "var(--app-pulsy-bg)",
      logos: ["pulsy"],
      href: "https://blog.buddieshr.com/how-to-create-a-survey-in-slack-2024/",
    },
    {
      tag: "Remote teams",
      t: "Best Slack apps for remote teams",
      d: "The add-ons remote companies are using to keep distributed teams close.",
      color: "var(--app-palmy)",
      bg: "var(--app-palmy-bg)",
      logos: ["alfy", "billy"],
      href: "https://blog.buddieshr.com/best-slack-apps-for-hybrid-and-remote-teams-update-2024/",
    },
  ];
  return (
    <section className="bh-section">
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <h2
          style={{
            textAlign: "center",
            fontSize: "clamp(28px, 3.6vw, 44px)",
            letterSpacing: "-0.03em",
            marginBottom: 44,
          }}
        >
          While it installs,{" "}
          <span style={{ fontStyle: "italic", color: "var(--bh-coral-400)" }}>
            some reading.
          </span>
        </h2>
        <div
          className="gs-steps"
          style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}
        >
          {posts.map((p, i) => (
            <a
              key={i}
              href={p.href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "block",
                textDecoration: "none",
                background: "#fff",
                border: "1.5px solid var(--bh-line)",
                borderRadius: "var(--bh-r-lg)",
                overflow: "hidden",
                boxShadow: "var(--bh-shadow-sm)",
                color: "inherit",
              }}
            >
              <div
                style={{
                  background: p.bg,
                  borderBottom: "1.5px solid var(--bh-line)",
                  height: 120,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 18,
                }}
              >
                {p.logos.map((id) => {
                  const app = byId[id];
                  if (!app) return null;
                  const Logo = app.Logo;
                  return (
                    <span
                      key={id}
                      style={{
                        flex: "0 0 auto",
                        width: 56,
                        height: 56,
                        display: "inline-flex",
                        justifyContent: "center",
                      }}
                    >
                      <Logo />
                    </span>
                  );
                })}
              </div>
              <div style={{ padding: "22px 26px 28px" }}>
                <div
                  style={{
                    display: "inline-block",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: ".1em",
                    textTransform: "uppercase",
                    color: p.color,
                    marginBottom: 14,
                  }}
                >
                  {p.tag}
                </div>
                <div
                  style={{
                    fontFamily: "var(--bh-font-display)",
                    fontSize: 21,
                    fontWeight: 600,
                    letterSpacing: "-0.015em",
                    lineHeight: 1.25,
                    marginBottom: 10,
                  }}
                >
                  {p.t}
                </div>
                <p
                  style={{
                    fontSize: 14.5,
                    color: "var(--bh-ink-soft)",
                    lineHeight: 1.55,
                    margin: 0,
                  }}
                >
                  {p.d}
                </p>
                <div
                  style={{
                    marginTop: 16,
                    fontSize: 14,
                    fontWeight: 600,
                    color: "var(--bh-coral-400)",
                  }}
                >
                  Read article <span className="bh-arrow">→</span>
                </div>
              </div>
            </a>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 36 }}>
          <a
            href="https://blog.buddieshr.com"
            target="_blank"
            rel="noopener noreferrer"
            className="bh-btn secondary"
            style={{
              textDecoration: "none",
            }}
          >
            More blog articles
          </a>
        </div>
      </div>
    </section>
  );
}
